import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const DISPOSITIFS = [
  "ALTER LOGEMENTS", "AVDL", "BAIL ACCOMPAGNE ASL", "CHRS CO LOCATION",
  "CHRS COLLECTIF ASILE DE NUIT", "CHRS COLLECTIF FEMME", "CHRS COLLECTIF RENAITRE",
  "CHRS COLLECTIF ROANNE", "CHRS DIFFUS", "CHRS HORS LES MURS",
  "DISPOSITIF SPECIFIQUE DEDIE AUX SORTANTS DU CD DE ROANNE",
  "DROITS INCOMPLETS", "FJT CLAIRVIVRE", "FJT ROANNE", "ILOT JEUNES",
  "IML FVV", "IML MANDAT DE GESTION", "IML SOUS LOCATION", "J'APPART",
  "LOGEMENTS PASSERELLE", "PENSION DE FAMILLE", "RESIDENCE ACCUEIL BAUDELAIRE",
  "RESIDENCE SOCIALE", "RESIDENCE SOCIALE JEUNES ASL",
];

const COULEURS = [
  { value: "blanc", label: "Sur liste d'attente",   dotCls: "bg-slate-500" },
  { value: "bleu",  label: "En cours d'orientation", dotCls: "bg-blue-500" },
  { value: "rouge", label: "A Supprimer",             dotCls: "bg-red-500" },
  { value: "gris",  label: "A Revoir",                dotCls: "bg-orange-400" },
];

const SEXE_OPTIONS = ["H", "F", "C"];
const COMPO_OPTIONS = [
  "HOMME ISOLE", "FEMME ISOLEE", "COUPLE SANS ENFANTS", "COUPLE AVEC ENFANTS",
  "FAMILLE MONOPARENTALE", "AUTRE",
];

const EMPTY = {
  dispositif: "", nom: "", prenom: "", date_naissance: "", sexe: "",
  composition_familiale: "", nb_enfants: null, degre_accompagnement: null,
  sante: "", date_demande: "", date_commission: "", secteur_geographique: "",
  commentaire: "", referent: "", date_orientation: "", priorite: null, couleur: "blanc", ressource: "",
};

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white";

export default function WaitlistEntryModal({ entry, onSave, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(entry ? { ...EMPTY, ...entry } : EMPTY);
  }, [entry]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await onSave(form); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      {/* Modal — max largeur paysage */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: "min(96vw, 1100px)", maxHeight: "94vh", fontFamily: "Manrope, sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            {entry ? "Modifier l'entrée" : "Nouvelle entrée"}
          </h2>
          <button onClick={onClose} data-testid="modal-close-btn"
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body — scroll uniquement si nécessaire */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-7 py-5">

          {/* Row 0 — Dispositif (pleine largeur) */}
          <div className="mb-4">
            <Label required>Dispositif</Label>
            <select value={form.dispositif} onChange={e => set("dispositif", e.target.value)}
              required data-testid="field-dispositif" className={inputCls}>
              <option value="">— Sélectionner —</option>
              {DISPOSITIFS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Grid 3 colonnes */}
          <div className="grid grid-cols-3 gap-x-5 gap-y-3">

            {/* Identité */}
            <div>
              <Label>Nom</Label>
              <input value={form.nom || ""} onChange={e => set("nom", e.target.value)}
                data-testid="field-nom" className={inputCls} />
            </div>
            <div>
              <Label>Prénom</Label>
              <input value={form.prenom || ""} onChange={e => set("prenom", e.target.value)}
                data-testid="field-prenom" className={inputCls} />
            </div>
            <div>
              <Label>Date de naissance</Label>
              <input value={form.date_naissance || ""} onChange={e => set("date_naissance", e.target.value)}
                placeholder="JJ/MM/AAAA" data-testid="field-date_naissance" className={inputCls} />
            </div>

            <div>
              <Label>Sexe</Label>
              <select value={form.sexe || ""} onChange={e => set("sexe", e.target.value)}
                data-testid="field-sexe" className={inputCls}>
                <option value="">— Sélectionner —</option>
                {SEXE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label>Composition familiale</Label>
              <select value={form.composition_familiale || ""} onChange={e => set("composition_familiale", e.target.value)}
                data-testid="field-composition_familiale" className={inputCls}>
                <option value="">— Sélectionner —</option>
                {COMPO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label>Nombre d'enfants</Label>
              <input type="number" min={0} value={form.nb_enfants ?? ""}
                onChange={e => set("nb_enfants", e.target.value ? parseInt(e.target.value) : null)}
                data-testid="field-nb_enfants" className={inputCls} />
            </div>

            {/* Demande */}
            <div>
              <Label>Date de la demande</Label>
              <input value={form.date_demande || ""} onChange={e => set("date_demande", e.target.value)}
                placeholder="JJ/MM/AAAA" data-testid="field-date_demande" className={inputCls} />
            </div>
            <div>
              <Label>Commission du</Label>
              <input value={form.date_commission || ""} onChange={e => set("date_commission", e.target.value)}
                placeholder="JJ/MM/AAAA" data-testid="field-date_commission" className={inputCls} />
            </div>
            <div>
              <Label>Priorité</Label>
              <input type="number" min={0} value={form.priorite ?? ""}
                onChange={e => set("priorite", e.target.value ? parseInt(e.target.value) : null)}
                data-testid="field-priorite" className={inputCls} />
            </div>

            <div>
              <Label>Degré d'accompagnement (1-3)</Label>
              <input type="number" min={1} max={3} value={form.degre_accompagnement ?? ""}
                onChange={e => set("degre_accompagnement", e.target.value ? parseInt(e.target.value) : null)}
                data-testid="field-degre_accompagnement" className={inputCls} />
            </div>
            <div>
              <Label>Santé</Label>
              <input value={form.sante || ""} onChange={e => set("sante", e.target.value)}
                data-testid="field-sante" className={inputCls} />
            </div>
            <div>
              <Label>Secteur géographique</Label>
              <input value={form.secteur_geographique || ""} onChange={e => set("secteur_geographique", e.target.value)}
                data-testid="field-secteur_geographique" className={inputCls} />
            </div>

            <div>
              <Label>Référent</Label>
              <input value={form.referent || ""} onChange={e => set("referent", e.target.value)}
                data-testid="field-referent" className={inputCls} />
            </div>
            <div>
              <Label>Ressource</Label>
              <input value={form.ressource || ""} onChange={e => set("ressource", e.target.value)}
                data-testid="field-ressource" className={inputCls} />
            </div>
            <div>
              <Label>Date d'orientation</Label>
              <input value={form.date_orientation || ""} onChange={e => set("date_orientation", e.target.value)}
                placeholder="JJ/MM/AAAA" data-testid="field-date_orientation" className={inputCls} />
            </div>
            <div /> {/* spacer */}
          </div>

          {/* Statut couleur + Commentaire côte à côte */}
          <div className="grid grid-cols-2 gap-x-5 mt-3">
            {/* Couleur */}
            <div>
              <Label>Statut / Couleur</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COULEURS.map(({ value, label, dotCls }) => (
                  <button key={value} type="button" onClick={() => set("couleur", value)}
                    data-testid={`couleur-option-${value}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                      form.couleur === value ? "border-indigo-500 shadow-sm bg-indigo-50" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}>
                    <span className={`w-3 h-3 rounded-full flex-shrink-0 ${dotCls}`} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Commentaire */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Commentaire
                {form.date_commentaire && (
                  <span className="ml-2 text-indigo-500 font-normal normal-case tracking-normal text-xs">
                    — dernière modif. : {form.date_commentaire}
                  </span>
                )}
              </label>
              <textarea value={form.commentaire || ""} onChange={e => set("commentaire", e.target.value)}
                rows={3} data-testid="field-commentaire"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={loading} data-testid="modal-save-btn"
              className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50">
              {loading ? "Enregistrement..." : (entry ? "Modifier" : "Ajouter")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
